import { useMemo } from "react";
import { Chart } from "@highcharts/react";
import { useTranslation } from "react-i18next";
import { AD_TYPE_COLOR_MAP } from "../../../../components/core/blocks/AdPlan/constants";
import { formatCurrency, formatNumber, formatPercent } from "../../../../utils/formatNumber";
import { useAppSelector } from "../../../../redux/hooks";
import { adTypesByChannel } from "./AdTypeSection";

const AdTypeChart = ({ data, format }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const { t } = useTranslation(["advertising"]);

  const total = data?.reduce((acc, current) => {
    return acc + current.y;
  }, 0);

  const options = useMemo(() => {
    if (!data) {
      return {};
    }

    return {
      chart: {
        type: "pie",
        style: {
          fontSize: "16px",
        },
      },
      title: {
        text: "",
        style: {
          fontSize: "18px",
          fontWeight: 400,
          align: "left",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function () {
              return (
                `<span style="font-size: 1.2em; font-weight: 600; opacity: 0.8">${this.point.name}</span><br>` +
                `<span style="opacity: 0.6; margin: auto; display: block; text-align: center; width: 100%;">${
                  format === "currency" ?
                    formatCurrency(this.point.y, {
                      marketPlace: marketPlace.marketPlace,
                    })
                  : formatNumber(this.point.y)
                } - ${formatPercent(this.point.y / total, {
                  decimalPlaces: 1,
                })}</span>`
              );
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const formattedValue =
            format === "currency" ?
              formatCurrency(this.point.y, {
                marketPlace: marketPlace.marketPlace,
              })
            : formatNumber(this.point.y);

          if (total) {
            return (
              "<span><strong>" +
              this.point.name +
              ":</strong><br />" +
              formattedValue +
              "<strong>" +
              ` (${formatPercent(this.point.y / total, {
                decimalPlaces: 1,
              })})` +
              "</strong></span>"
            );
          }

          return "<span><strong>" + this.point.name + ":</strong><br />" + formattedValue + "</span>";
        },
      },
      series: [
        {
          data:
            adTypesByChannel[channel.currentChannel ?? "amazon"]?.map((type) => {
              const value = data?.find((row) => row.ad_type === type.value)?.y;
              return {
                id: type.value,
                name: t(type.label),
                y: value,
                color: AD_TYPE_COLOR_MAP[type.value],
              };
            }) ?? [],
        },
      ],
    };
  }, [data, marketPlace.marketPlace, channel.currentChannel, total, format, t]);

  return <Chart options={options} />;
};

export default AdTypeChart;
